import React, { useEffect, useState } from "react";

import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router";

import { ONBOARDING, AUTH, ERROR } from "../../../platform/constants/routes";
import { EventBus } from "../../../platform/events/EventBus";
import { StateEvent } from "../../../platform/state/types";
import debugLogger from "../../../platform/utils/debugLogger";

interface RecoveryStep {
  id: string;
  name: string;
  description: string;
  status: "pending" | "checking" | "completed" | "failed";
  result?: string;
}

interface RecoveryPageProps {
  eventBus?: EventBus;
}

export const RecoveryPage: React.FC<RecoveryPageProps> = ({ eventBus }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [recoverySteps, setRecoverySteps] = useState<RecoveryStep[]>([
    {
      id: "session",
      name: "Session Check",
      description: "Checking for existing user session",
      status: "pending",
    },
    {
      id: "auth",
      name: "Authentication",
      description: "Validating authentication tokens",
      status: "pending",
    },
    {
      id: "profile",
      name: "User Profile",
      description: "Loading user profile data",
      status: "pending",
    },
    {
      id: "organizations",
      name: "Organizations",
      description: "Checking user organization access",
      status: "pending",
    },
  ]);

  const [overallProgress, setOverallProgress] = useState(0);
  const [hasAuthData, setHasAuthData] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const startRecovery = async () => {
      try {
        setError(null);

        // Simulate recovery process
        for (let i = 0; i < recoverySteps.length; i++) {
          setCurrentStep(i);

          // Update step status to checking
          setRecoverySteps((prev) =>
            prev.map((step, index) =>
              index === i ? { ...step, status: "checking" } : step,
            ),
          );

          // Simulate checking time for each step
          await new Promise((resolve) =>
            setTimeout(resolve, 600 + Math.random() * 300),
          );

          // Simulate different results based on step
          let stepResult = "Found";
          let stepStatus: RecoveryStep["status"] = "completed";

          if (i === 0) {
            // Session check
            // Simulate 80% chance of finding session
            const hasSession = Math.random() > 0.2;
            stepResult = hasSession
              ? "Active session found"
              : "No active session";
            stepStatus = hasSession ? "completed" : "failed";
          } else if (i === 1) {
            // Auth check
            // Simulate 70% chance of valid auth
            const hasValidAuth = Math.random() > 0.3;
            stepResult = hasValidAuth
              ? "Valid authentication"
              : "Invalid or expired tokens";
            stepStatus = hasValidAuth ? "completed" : "failed";
          } else if (i === 2) {
            // Profile check
            // Simulate 90% chance of profile data
            const hasProfile = Math.random() > 0.1;
            stepResult = hasProfile
              ? "Profile data available"
              : "Profile data missing";
            stepStatus = hasProfile ? "completed" : "failed";
          } else if (i === 3) {
            // Organizations check
            // Simulate 60% chance of organization access
            const hasOrgs = Math.random() > 0.4;
            stepResult = hasOrgs
              ? "Organization access confirmed"
              : "No organization access";
            stepStatus = hasOrgs ? "completed" : "failed";
          }

          // Update step with result
          setRecoverySteps((prev) =>
            prev.map((step, index) =>
              index === i
                ? { ...step, status: stepStatus, result: stepResult }
                : step,
            ),
          );

          // Update overall progress
          setOverallProgress(((i + 1) / recoverySteps.length) * 100);
        }

        // Determine final result
        const completedSteps = recoverySteps.filter(
          (step) => step.status === "completed",
        ).length;
        const hasValidSession = completedSteps >= 2; // At least session and auth should be valid

        setHasAuthData(hasValidSession);

        // Wait a moment to show final state
        setTimeout(() => {
          if (hasValidSession) {
            // Emit auth data present event
            if (eventBus) {
              eventBus.emitGlobal(StateEvent.AUTH_DATA_PRESENT, {
                timestamp: new Date().toISOString(),
                steps: recoverySteps.map((step) => ({
                  id: step.id,
                  status: step.status,
                  result: step.result,
                })),
              });
            }

            // Navigate to onboarding phase
            void navigate(ONBOARDING.ROOT.path);
          } else {
            // Emit auth data missing event
            if (eventBus) {
              eventBus.emitGlobal(StateEvent.AUTH_DATA_MISSING, {
                timestamp: new Date().toISOString(),
                steps: recoverySteps.map((step) => ({
                  id: step.id,
                  status: step.status,
                  result: step.result,
                })),
              });
            }

            // Navigate to authentication phase
            void navigate(AUTH.LOGIN.path);
          }
        }, 1000);
      } catch (error) {
        debugLogger.error("Recovery failed:", error);
        setError(
          error instanceof Error ? error.message : "Unknown error occurred",
        );

        // Emit error event
        if (eventBus) {
          eventBus.emitGlobal(StateEvent.ERROR_OCCURRED, {
            error: error instanceof Error ? error.message : "Unknown error",
            context: "recovery",
            timestamp: new Date().toISOString(),
          });
        }

        // Navigate to error page after delay
        setTimeout(() => {
          void navigate(ERROR.path);
        }, 2000);
      }
    };

    void startRecovery();
  }, [eventBus, navigate]);

  const getStepIcon = (status: RecoveryStep["status"]) => {
    switch (status) {
      case "completed":
        return "✓";
      case "failed":
        return "✗";
      case "checking":
        return <CircularProgress size={16} />;
      default:
        return "○";
    }
  };

  const getStepColor = (status: RecoveryStep["status"]) => {
    switch (status) {
      case "completed":
        return "success.main";
      case "failed":
        return "error.main";
      case "checking":
        return "primary.main";
      default:
        return "text.secondary";
    }
  };

  const handleManualLogin = () => {
    void navigate(AUTH.LOGIN.path);
  };

  const handleRetry = () => {
    // Reset and restart recovery
    setCurrentStep(0);
    setOverallProgress(0);
    setHasAuthData(null);
    setError(null);
    setRecoverySteps((prev) =>
      prev.map((step) => ({
        ...step,
        status: "pending" as const,
        result: undefined,
      })),
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      {/* Main Recovery Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 600,
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Session Recovery
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Checking your authentication status
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Overall Progress */}
        <Box sx={{ width: "100%", mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Recovery Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(overallProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        {/* Recovery Steps */}
        <Card sx={{ width: "100%", mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recovery Steps
            </Typography>

            {recoverySteps.map((step, _index) => (
              <Box
                key={step.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: "background.default",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                {/* Step Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    mr: 2,
                    color: getStepColor(step.status),
                  }}
                >
                  {getStepIcon(step.status)}
                </Box>

                {/* Step Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {step.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                  {step.result && (
                    <Typography
                      variant="caption"
                      color={
                        step.status === "completed"
                          ? "success.main"
                          : "error.main"
                      }
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {step.result}
                    </Typography>
                  )}
                </Box>

                {/* Step Status */}
                <Typography
                  variant="caption"
                  color={getStepColor(step.status)}
                  sx={{ textTransform: "capitalize" }}
                >
                  {step.status}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Result Display */}
        {hasAuthData !== null && (
          <Card sx={{ width: "100%", mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recovery Result
              </Typography>
              {hasAuthData ? (
                <Alert severity="success">
                  Session recovered successfully! Redirecting to workspace...
                </Alert>
              ) : (
                <Alert severity="info">
                  No valid session found. Please log in to continue.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {overallProgress === 100 && hasAuthData === false && (
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleManualLogin}
              size="large"
            >
              Log In
            </Button>
            <Button variant="outlined" onClick={handleRetry} size="large">
              Retry Recovery
            </Button>
          </Box>
        )}

        {/* Current Step Indicator */}
        {currentStep < recoverySteps.length && overallProgress < 100 && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Currently checking: {recoverySteps[currentStep]?.name}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecoveryPage;
